/**
 * @customElement link-element
 * @description A customizable link button component that supports dynamic styling and text.
 * 
 * @example
 * <!-- Basic usage -->
 * <link-element label="Sign Up" url="https://example.com"></link-element>
 * 
 * @example
 * <!-- With custom styling -->
 * <link-element 
 *   label="Get Started"
 *   url="https://example.com"
 *   settings='{
 *     "backgroundColor": "#4CAF50",
 *     "borderColor": "#45a049",
 *     "hoverBackgroundColor": "#45a049",
 *     "textColor": "#ffffff",
 *     "fontSize": "16px",
 *     "borderRadius": "8px"
 *   }'
 * ></link-element>
 * 
 * @attr {string} url - The URL the link should navigate to
 * @attr {string} label - The text to display on the button (defaults to "Subscribe")
 * @attr {string} settings - JSON string containing styling options:
 *   @property {string} backgroundColor - Button background color (default: "#0078d4")
 *   @property {string} borderColor - Button border color (default: "#0078d4")
 *   @property {string} hoverBackgroundColor - Background color on hover (default: "#005fa3")
 *   @property {string} hoverBorderColor - Border color on hover (default: "#005fa3")
 *   @property {string} activeBackgroundColor - Background color when active (default: "#00497a")
 *   @property {string} activeBorderColor - Border color when active (default: "#00497a")
 *   @property {string} textColor - Text color (default: "#fff")
 *   @property {string} fontSize - Font size (default: "15px")
 *   @property {string} borderRadius - Border radius (default: "4px")
 *   @property {string} padding - Button padding (default: "8px 8px")
 *   @property {string} borderWidth - Border width (default: "1px")
 *   @property {string} boxShadow - Box shadow (default: "0 2px 4px rgba(0,0,0,0.06)")
 */

import { defaultStyles, generateStyles } from './linkElement-styles.js';

class LinkElement extends HTMLElement {
    constructor() {
      super();
      this.shadow = this.attachShadow({ mode: 'open' });
      this._url = 'https://lovebowls.co.uk/';
      this._label = 'Subscribe';
      this._settings = {};
      this._isMobile = false;
    }
  
    static get observedAttributes() {
      return ['url', 'label', 'settings', 'is-mobile'];
    }
  
    connectedCallback() {
      this._url = this.getAttribute('url') || 'https://lovebowls.co.uk/';
      this._label = this.getAttribute('label') || 'Subscribe';
      this._settings = this._parseSettings(this.getAttribute('settings'));
      this._isMobile = this.hasAttribute('is-mobile');
      this.render();
    }
  
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      
      switch (name) {
        case 'url':
          this._url = newValue || 'https://lovebowls.co.uk/';
          break;
        case 'label':
          this._label = newValue || 'Subscribe';
          break;
        case 'settings':
          this._settings = this._parseSettings(newValue);
          break;
        case 'is-mobile':
          this._isMobile = this.hasAttribute('is-mobile');
          break;
      }
      this.render();
    }

    _parseSettings(settingsString) {
      try {
        return settingsString ? JSON.parse(settingsString) : {};
      } catch (error) {
        console.warn('Invalid settings JSON:', error);
        return {};
      }
    }

    _getStyles() {
      const baseStyles = { ...defaultStyles };
      
      // Merge custom settings if provided
      if (this._settings.desktop) {
        baseStyles.desktop = { ...baseStyles.desktop, ...this._settings.desktop };
      }
      if (this._settings.mobile) {
        baseStyles.mobile = { ...baseStyles.mobile, ...this._settings.mobile };
      }
      
      return baseStyles;
    }
  
    render() {
      const styles = this._getStyles();
  
      this.shadow.innerHTML = `
        <style>
          ${generateStyles(styles, this._isMobile)}
        </style>
        <div class="container">
          <a class="subscribe-link" href="${this._url}" target="_self" tabindex="0">${this._label}</a>
        </div>
      `;
    }
  }
  
  customElements.define('link-element', LinkElement);
  
  export default LinkElement;
  